/**
 * CommonMark inline links do not allow unescaped spaces in the destination
 * unless it is wrapped in `<...>`. Blob SAS URLs often include a filename with
 * spaces; remark then leaves `[text](url)` as plain text. Encode spaces so the
 * link parses and the href is valid.
 */
function normalizeMarkdownLinkDestination(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    return trimmed;
  }
  if (trimmed.startsWith("<") && trimmed.endsWith(">")) {
    return trimmed;
  }
  if (!/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  if (!/\s/.test(trimmed)) {
    return trimmed;
  }
  return trimmed.replace(/ /g, "%20");
}

function parseLinkDestination(
  text: string,
  innerStart: number
): { endExclusive: number; raw: string } | null {
  let pos = innerStart;
  while (pos < text.length && /\s/.test(text[pos])) {
    pos++;
  }
  if (pos >= text.length) {
    return null;
  }

  if (text[pos] === "<") {
    const gt = text.indexOf(">", pos + 1);
    if (gt === -1) {
      return null;
    }
    let closeParen = gt + 1;
    while (closeParen < text.length && /\s/.test(text[closeParen])) {
      closeParen++;
    }
    if (text[closeParen] !== ")") {
      return null;
    }
    return {
      endExclusive: closeParen + 1,
      raw: text.slice(innerStart, closeParen).trim(),
    };
  }

  let depth = 0;
  for (let j = pos; j < text.length; j++) {
    const c = text[j];
    if (c === "(") {
      depth++;
    } else if (c === ")") {
      if (depth === 0) {
        return {
          endExclusive: j + 1,
          raw: text.slice(innerStart, j).trim(),
        };
      }
      depth--;
    }
  }
  return null;
}

function findNextMarkdownLink(
  text: string,
  from: number
): { start: number; endExclusive: number; linkText: string; dest: string } | null {
  const open = text.indexOf("[", from);
  if (open === -1) {
    return null;
  }

  let i = open + 1;
  let depth = 0;
  while (i < text.length) {
    const c = text[i];
    if (c === "[") {
      depth++;
    } else if (c === "]") {
      if (depth === 0) {
        if (text[i + 1] === "(") {
          const parsed = parseLinkDestination(text, i + 2);
          if (parsed) {
            return {
              start: open,
              endExclusive: parsed.endExclusive,
              linkText: text.slice(open + 1, i),
              dest: parsed.raw,
            };
          }
        }
        i++;
        continue;
      }
      depth--;
    }
    i++;
  }

  return findNextMarkdownLink(text, open + 1);
}

function encodeSpacesInMarkdownHttpLinks(text: string): string {
  let from = 0;
  let out = "";
  while (from < text.length) {
    const link = findNextMarkdownLink(text, from);
    if (!link) {
      out += text.slice(from);
      break;
    }
    out += text.slice(from, link.start);
    const dest = normalizeMarkdownLinkDestination(link.dest);
    out += `[${link.linkText}](${dest})`;
    from = link.endExclusive;
  }
  return out;
}

/**
 * Citation chunks from search/indexing often lose newlines; ReactMarkdown then
 * sees a single paragraph. Restore breaks before common block-level constructs.
 */
function repairFlattenedMarkdownProse(input: string): string {
  let s = input;
  s = s.replace(/\*\*(\s*\d[\d.]*\s[^*]+)\*\*/g, (_m, inner: string) => {
    let x = inner.replace(
      /([A-Z][a-z]{2,})([A-Z][a-z]{2,})/g,
      "$1 $2"
    );
    x = x.replace(/([a-z]{2,})([A-Z][a-z]{2,})/g, "$1 $2");
    return `**${x}**`;
  });
  s = s.replace(/(\*\*[^*]+\*\*)([A-Z][a-z])/g, "$1\n\n$2");
  for (let i = 0; i < 2; i++) {
    s = s.replace(/([A-Z][a-z]{2,})([A-Z][a-z]{2,})/g, "$1 $2");
    s = s.replace(/([a-z]{2,})([A-Z][a-z]{2,})\b/g, "$1 $2");
  }
  s = s.replace(/([a-z0-9])([.!?])([A-Z][a-z]{2,}\b)/g, "$1$2 $3");
  s = s.replace(/([a-z])(\d+\.\s+[a-z])/g, "$1\n$2");
  return s;
}

export function normalizeCitationMarkdownPreview(text: string): string {
  if (!text?.trim()) {
    return text;
  }
  let s = text.replace(/\r\n/g, "\n");
  s = repairFlattenedMarkdownProse(s);

  s = s.replace(/(?<!\n)(---)(#+)/g, "\n---\n$2");
  s = s.replace(/(?<=[^\n#])(#{1,6}\s)/g, "\n$1");
  s = s.replace(/([:;])\s*(\d+\.\s+[A-Za-z])/g, "$1\n$2");
  s = s.replace(/([a-z])\.(\d+\.\s+[A-Z])/g, "$1.\n$2");
  s = s.replace(/([a-z])(-\s+[A-Z][a-z])/g, "$1\n$2");
  s = s.replace(/([.!?:)])(-\s+[A-Z][a-z])/g, "$1\n$2");

  const lines = s.split("\n");
  const fixed = lines.map((line) => {
    if (/^\s*#{1,6}\s/.test(line)) {
      return line.replace(/([a-z]{2,})([A-Z][a-z]+\s+\d)/g, "$1\n$2");
    }
    return line;
  });
  s = fixed.join("\n");
  s = s.replace(/\n{3,}/g, "\n\n");
  s = encodeSpacesInMarkdownHttpLinks(s);
  return s.trim();
}
